import { useState, useEffect, useCallback, useMemo } from 'react';
import { SearchBar } from './Nav';
import { DataDisplay, DateRange, formatDate, formatValue, SearchSelect, Toggle } from './FieldComponents';
import Modal from './Modal';
import { Minus, Plus } from 'lucide-react';
import { CreatePayment } from './CreatePayment';
import {
    buildPaymentRequest,
    createFreshPaymentData,
    getActivePaymentTotal,
    getPaymentAllocationSummary,
    getPaymentValidationErrors,
    paymentIsVoid,
} from './TimeBilling';
import { getContactInitialsClassName } from '../utils/colorUtils';
import { fetchNavSearchResults, toCaseSelectionOptions } from '../utils/navSearch';

const normalizePaymentDate = (value) => String(value || '').match(/^\d{4}-\d{2}-\d{2}/)?.[0] || '';

const getCaseId = (caseItem) => Number(caseItem?.case_id ?? caseItem?.id);

const getCaseName = (caseItem, caseId) => (
    caseItem?.case_name || caseItem?.name || `Case #${caseId}`
);

const getCaseInitials = (caseItem, caseId) => {
    const contactDisplay = String(caseItem?.contact_display || '').trim();
    if (contactDisplay && !contactDisplay.includes('uploads')) return contactDisplay;
    const source = String(caseItem?.contact_name || caseItem?.case_name || '').trim();
    const initials = source.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase();
    return initials || String(caseId).slice(-2);
};

export const filterBillingPayments = ({
    payments = [],
    caseById = new Map(),
    caseQuery = '',
    paymentQuery = '',
    dateRange = {},
    showVoid = true,
} = {}) => {
    const normalizedCaseQuery = String(caseQuery || '').trim().toLowerCase();
    const normalizedPaymentQuery = String(paymentQuery || '').trim().toLowerCase();
    const startDate = normalizePaymentDate(dateRange?.start);
    const endDate = normalizePaymentDate(dateRange?.end);

    return (Array.isArray(payments) ? payments : []).filter(paymentData => {
        if (!showVoid && paymentIsVoid(paymentData)) return false;
        const paymentDate = normalizePaymentDate(paymentData?.date);
        if (startDate && (!paymentDate || paymentDate < startDate)) return false;
        if (endDate && (!paymentDate || paymentDate > endDate)) return false;

        const caseId = Number(paymentData?.case_id);
        const caseItem = caseById.get(caseId);
        if (normalizedCaseQuery) {
            const caseText = [
                caseId,
                paymentData?.case_name,
                paymentData?.contact_name,
                caseItem?.case_name,
                caseItem?.name,
                caseItem?.contact_name,
            ].map(value => String(value || '').toLowerCase()).join(' ');
            if (!caseText.includes(normalizedCaseQuery)) return false;
        }

        if (normalizedPaymentQuery) {
            const allocationText = (Array.isArray(paymentData?.allocations) ? paymentData.allocations : [])
                .map(allocation => `${allocation?.invoice_number || ''} ${allocation?.invoice_id || ''}`)
                .join(' ');
            const paymentText = [
                paymentData?.method,
                paymentData?.reference,
                paymentData?.status,
                allocationText,
            ].map(value => String(value || '').toLowerCase()).join(' ');
            if (!paymentText.includes(normalizedPaymentQuery)) return false;
        }

        return true;
    });
};

export const groupBillingPaymentsByCase = (payments = []) => Array.from(
    (Array.isArray(payments) ? payments : []).reduce((groups, paymentData) => {
        const caseId = Number(paymentData?.case_id);
        const existing = groups.get(caseId) || {
            case_id: caseId,
            case_name: paymentData?.case_name || '',
            contact_name: paymentData?.contact_name || '',
            contact_display: paymentData?.contact_display || '',
            total: 0,
            void_count: 0,
            payments: [],
        };
        if (!existing.case_name && paymentData?.case_name) existing.case_name = paymentData.case_name;
        if (!existing.contact_name && paymentData?.contact_name) existing.contact_name = paymentData.contact_name;
        if (!existing.contact_display && paymentData?.contact_display) existing.contact_display = paymentData.contact_display;
        if (paymentIsVoid(paymentData)) existing.void_count += 1;
        else existing.total += Number(paymentData?.total || 0);
        existing.payments.push(paymentData);
        groups.set(caseId, existing);
        return groups;
    }, new Map()).values()
);

export const BillingPayments = ({ cases }) => {
    const [searchCase, setSearchCase] = useState('');
    const [searchPayment, setSearchPayment] = useState('');
    const [paymentDateRange, setPaymentDateRange] = useState({ start: '', end: '' });
    const [showVoid, setShowVoid] = useState(true);
    const [createPayment, setCreatePayment] = useState(false);
    const [payments, setPayments] = useState([]);
    const [payment, setPayment] = useState(null);
    const [selectedCase, setSelectedCase] = useState(null);
    const [expandedPaymentCaseId, setExpandedPaymentCaseId] = useState(null);
    const [paymentErrors, setPaymentErrors] = useState({});
    const [paymentError, setPaymentError] = useState('');
    const [isSavingPayment, setIsSavingPayment] = useState(false);
    const [isLoadingPaymentDetails, setIsLoadingPaymentDetails] = useState(false);
    const [paymentInvoices, setPaymentInvoices] = useState([]);
    const [paymentAllocationsAvailable, setPaymentAllocationsAvailable] = useState(false);
    const [paymentInvoiceNotice, setPaymentInvoiceNotice] = useState('');

    const caseOptions = useMemo(() => (Array.isArray(cases) ? cases : []).map(caseItem => ({
        value: String(caseItem?.case_id ?? caseItem?.id),
        label: caseItem?.case_name || caseItem?.name || `Case ${caseItem?.case_id ?? caseItem?.id}`,
    })), [cases]);
    const caseById = useMemo(() => new Map(
        (Array.isArray(cases) ? cases : [])
            .map(caseItem => [getCaseId(caseItem), caseItem])
            .filter(([caseId]) => Number.isInteger(caseId) && caseId > 0)
    ), [cases]);
    const filteredPayments = useMemo(() => filterBillingPayments({
        payments,
        caseById,
        caseQuery: searchCase,
        paymentQuery: searchPayment,
        dateRange: paymentDateRange,
        showVoid,
    }), [caseById, paymentDateRange, payments, searchCase, searchPayment, showVoid]);
    const groupedPayments = useMemo(
        () => groupBillingPaymentsByCase(filteredPayments),
        [filteredPayments]
    );

    const loadCaseOptions = useCallback(async (query) => {
        const results = await fetchNavSearchResults(query, { type: 'cases', limit: 25 });
        return toCaseSelectionOptions(results);
    }, []);

    const fetchPayments = async () => {
        try {
            const response = await fetch('https://api.casedb.co/payments.php', {
                credentials: 'include'
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || `Payments could not be loaded (${response.status}).`);
            }
            setPayments(data.payments || []);
            setPaymentAllocationsAvailable(data.payment_allocations_available === true);
        } catch (error) {
            console.error('Error fetching payments:', error);
        }
    };

    const postPayment = async () => {
        if (isSavingPayment) return;
        const validationErrors = getPaymentValidationErrors(payment);
        if (Object.keys(validationErrors).length > 0) {
            setPaymentErrors(validationErrors);
            setPaymentError('Correct the highlighted payment details and try again.');
            return;
        }

        setIsSavingPayment(true);
        setPaymentErrors({});
        setPaymentError('');
        try {
            const response = await fetch('https://api.casedb.co/payments.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(buildPaymentRequest(payment, selectedCase))
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok || !data.success) {
                setPaymentError(data.message || `The payment could not be saved (${response.status}).`);
                return;
            }
            await fetchPayments();
            setPayment(null);
            setSelectedCase(null);
            setCreatePayment(false);
        } catch (error) {
            console.error('Error posting payment:', error);
            setPaymentError(error.message || 'The payment could not be saved. Please try again.');
        } finally {
            setIsSavingPayment(false);
        }
    };

    const closePaymentModal = () => {
        if (isSavingPayment || isLoadingPaymentDetails) return;
        setCreatePayment(false);
        setSelectedCase(null);
        setPayment(null);
        setPaymentErrors({});
        setPaymentError('');
        setPaymentInvoices([]);
        setPaymentInvoiceNotice('');
    };

    const openPaymentDetails = async () => {
        const caseId = Number(selectedCase);
        if (!Number.isInteger(caseId) || caseId <= 0) {
            setPaymentError('Select a case before continuing.');
            return;
        }
        if (isLoadingPaymentDetails) return;

        setIsLoadingPaymentDetails(true);
        setPaymentInvoices([]);
        setPaymentInvoiceNotice('');
        if (paymentAllocationsAvailable) {
            try {
                const response = await fetch(
                    `https://api.casedb.co/billing_entries.php?case_id=${caseId}&invoices=true`,
                    { credentials: 'include' }
                );
                const data = await response.json().catch(() => ({}));
                if (!response.ok || !data.success) {
                    throw new Error(data.message || `Invoices could not be loaded (${response.status}).`);
                }
                setPaymentInvoices(data.invoices || []);
            } catch (error) {
                console.error('Error loading invoices for payment:', error);
                setPaymentInvoiceNotice(
                    'Invoice choices could not be loaded. You can still save this payment without applying it to an invoice.'
                );
            }
        }
        setPayment(createFreshPaymentData(caseId));
        setPaymentErrors({});
        setPaymentError('');
        setCreatePayment(2);
        setIsLoadingPaymentDetails(false);
    };

    const updatePaymentDraft = (nextValue) => {
        setPayment(previousValue => typeof nextValue === 'function'
            ? nextValue(previousValue)
            : nextValue);
        if (Object.keys(paymentErrors).length > 0) setPaymentErrors({});
        if (paymentError) setPaymentError('');
    };

    useEffect(() => {
        fetchPayments();
    }, []);

    return (
        <>
            <div className='billing payments'>
                <div className='payments-search'>
                    <div className='payments-search-header'>
                        <SearchBar placeholder='Case Name...' title='Search by Case Name' setSearchQuery={(val) => setSearchCase(val)} expanded={true} />
                        <SearchBar placeholder='Method or reference...' title='Search by Method or Reference' setSearchQuery={(val) => setSearchPayment(val)} expanded={true} />
                        <DateRange title='Payment Date' value={paymentDateRange} onChange={setPaymentDateRange} />
                        <Toggle
                            label='Include Void'
                            value={showVoid}
                            onChange={() => setShowVoid(prev => !prev)}
                        />
                    </div>
                    <button className='action tertiary' onClick={() => {
                        setPaymentError('');
                        setPaymentInvoiceNotice('');
                        setCreatePayment(1);
                    }}><Plus size={18} />Create Payment</button>
                </div>
                <div className='billing-container'>
                    <DataDisplay title='Payments' value={getActivePaymentTotal(filteredPayments)} type='currency' />
                    {groupedPayments.map((paymentGroup) => {
                        const caseItem = {
                            ...paymentGroup,
                            ...(caseById.get(paymentGroup.case_id) || {}),
                        };
                        const contactDisplay = String(caseItem?.contact_display || '').trim();
                        const contactImage = contactDisplay.includes('uploads');
                        const expanded = expandedPaymentCaseId === paymentGroup.case_id;
                        return (
                        <div
                            key={paymentGroup.case_id}
                            className={`payment-item ${expanded ? 'expanded' : ''}`}
                            role='button'
                            tabIndex={0}
                            aria-expanded={expanded}
                            onClick={() => setExpandedPaymentCaseId(expanded ? null : paymentGroup.case_id)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setExpandedPaymentCaseId(expanded ? null : paymentGroup.case_id);
                                }
                            }}
                        >
                            <div className='payment-summary'>
                                <div className='payment-details'>
                                    <div className='case-name-item'>
                                        {contactImage
                                            ? <img className='contact-initials' src={`https://api.casedb.co/${contactDisplay}`} alt='' />
                                            : <span className={getContactInitialsClassName(paymentGroup.case_id)}>{getCaseInitials(caseItem, paymentGroup.case_id)}</span>}
                                        <span>{getCaseName(caseItem, paymentGroup.case_id)}</span>
                                    </div>
                                    <div className='payment-total'>
                                        <span className='subtext'>{formatValue(paymentGroup.total, 'currency')}</span>
                                        {paymentGroup.void_count > 0 && (
                                            <span className='tag caps payment-void-tag'>
                                                {paymentGroup.void_count} Void
                                            </span>
                                        )}
                                        <div className='expand-icon'>
                                            {expanded ? <Minus size={14} /> : <Plus size={14} />}
                                        </div>
                                    </div>
                                </div>
                                {expanded && (
                                    <table className='payment-details-table table-full default'>
                                        <thead>
                                            <tr className='default'>
                                                <th>Date</th>
                                                <th>Method</th>
                                                <th>Reference #</th>
                                                <th>Invoice</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paymentGroup.payments.map(paymentData => (
                                                <tr key={paymentData.id} className={`default ${paymentIsVoid(paymentData) ? 'billing-void-transaction' : ''}`}>
                                                    <td>{formatDate(paymentData.date)[3]}</td>
                                                    <td>{paymentData.method}</td>
                                                    <td>{paymentData.reference ?? '(blank)'}</td>
                                                    <td>{getPaymentAllocationSummary(paymentData)}</td>
                                                    <td>
                                                        <span>{formatValue(paymentData.total, 'currency')}</span>
                                                        {paymentIsVoid(paymentData) && <span className='tag caps payment-void-tag'>Void</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                        );
                    })}
                    {groupedPayments.length === 0 && (
                        <div className='empty-state'>No payments match the current filters.</div>
                    )}
                </div>
            </div>
            {createPayment === 1 ? (
                <Modal
                    onClose={() => {
                        closePaymentModal();
                    }}
                    title="Create Payment"
                    single
                    footer={(
                        <div className='modal-footer-actions'>
                            <button
                                className='action alt'
                                onClick={() => {
                                    closePaymentModal();
                                }}
                                disabled={isLoadingPaymentDetails}
                            >
                                Cancel
                            </button>
                            <button
                                className='action' 
                                onClick={openPaymentDetails}
                                disabled={isLoadingPaymentDetails}
                            >
                                {isLoadingPaymentDetails ? 'Loading…' : 'Continue'}
                            </button>
                        </div>
                    )}
                >
                    <div className='modal-content-wrapper'>
                        {paymentError && <div className='payment-form-error' role='alert'>{paymentError}</div>}
                        <div className='form-group small'>
                            <label className='subtext'>Select Case</label>
                            <SearchSelect
                                options={caseOptions}
                                loadOptions={loadCaseOptions}
                                loadOptionsMinChars={0}
                                value={selectedCase}
                                onChange={(val) => {
                                    setSelectedCase(val);
                                    if (paymentError) setPaymentError('');
                                }}
                                placeholder='Search cases...'
                                portal
                            />
                        </div>
                    </div>
                </Modal>
            ) : createPayment === 2 && (
                <Modal
                    onClose={() => {
                        closePaymentModal();
                    }}
                    title="Payment"
                    single
                    contact_title={Array.isArray(cases) ? cases?.find(c => Number(c.case_id) === Number(selectedCase)) : null}
                    footer={(
                        <div className='modal-footer-actions'>
                            <button
                                className='action alt'
                                onClick={() => {
                                    closePaymentModal();
                                }}
                                disabled={isSavingPayment}
                            >
                                Cancel
                            </button>
                            <button
                                className='action'
                                onClick={postPayment}
                                disabled={isSavingPayment}
                            >
                                {isSavingPayment ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    )}
                >
                    <div className='modal-content-wrapper'>
                        {paymentInvoiceNotice && <div className='payment-allocation-notice' role='status'>{paymentInvoiceNotice}</div>}
                        <CreatePayment
                            payment={payment}
                            setPayment={updatePaymentDraft}
                            invoices={paymentAllocationsAvailable ? paymentInvoices : null}
                            errors={paymentErrors}
                            apiError={paymentError}
                            disabled={isSavingPayment}
                            embedded
                        />
                    </div>
                </Modal>
            )}
        </>
    );
};
